/**
 * Configurações › Sistema › Orçamento da Manutenção (mig 782; só o admin, D56). Nasce com o padrão do mockup aprovado:
 * nada fica esperando o Matheus (B3). Cada campo salva sozinho ao sair dele; a tela diz o que foi salvo ou a frase do erro.
 *
 * ⚠️ O markup padrão e o do técnico APONTAM para o catálogo do Comercial: escolher aqui não muda o padrão do Comercial (R2).
 */
import { useEffect, useState } from 'react';

import { Button, HelpTip, Input, Label, Select } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { reaisStringToCentavos } from '@/lib/format';
import { cn } from '@/lib/cn';

import { brl } from './formato';
import { mensagemDoErro, orcamentoApi, type ConfigDoOrcamento } from './orcamento-api';

const GRUPO = 'rounded-lg border border-line bg-paper-surface p-4 dark:border-line-dark dark:bg-paper-dark-surface';
const reaisDoCampo = (c: number) => (c / 100).toFixed(2).replace('.', ',');

export function ConfiguracaoDoOrcamento() {
  const [c, setC] = useState<ConfigDoOrcamento | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const { show } = useToast();

  useEffect(() => {
    orcamentoApi.config().then(setC).catch((e) => setErro(mensagemDoErro(e, 'Não deu para abrir a configuração.')));
  }, []);

  async function salvar(fn: () => Promise<ConfigDoOrcamento>) {
    setErro(null);
    try {
      setC(await fn());
      show('Salvo.', { variant: 'success' });
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  if (!c) return erro ? <p role="alert" className="text-sm text-red-deep">{erro}</p> : <p className="text-sm text-ink-3">Abrindo…</p>;

  return (
    <div className="flex flex-col gap-4" data-testid="config-orcamento-manutencao">
      {erro ? (
        <p role="alert" className="text-sm font-semibold text-red-deep dark:text-red-glow">
          {erro}
        </p>
      ) : null}

      <section className={GRUPO}>
        <h3 className="mb-3 flex items-center gap-1 text-sm font-bold">
          Preço dos produtos <HelpTip texto="O preço é o custo do cadastro do Omie mais o markup. O colaborador começa no padrão e pode trocar por item; o técnico usa sempre o dele." />
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="cfg-mk-padrao">Markup padrão da Manutenção</Label>
            <Select id="cfg-mk-padrao" value={c.markupPadrao?.id ?? ''} onChange={(e) => void salvar(() => orcamentoApi.salvarConfig({ markupPadraoId: e.target.value }))}>
              {!c.markupPadrao ? <option value="">Escolha</option> : null}
              {c.markups.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.percentual}%
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="cfg-mk-tecnico">Markup do técnico (fixo)</Label>
            <Select id="cfg-mk-tecnico" value={c.markupTecnico?.id ?? ''} onChange={(e) => void salvar(() => orcamentoApi.salvarConfig({ markupTecnicoId: e.target.value }))}>
              {!c.markupTecnico ? <option value="">Escolha</option> : null}
              {c.markups.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.percentual}%
                </option>
              ))}
            </Select>
          </div>
        </div>
      </section>

      <section className={GRUPO}>
        <h3 className="mb-3 flex items-center gap-1 text-sm font-bold">
          Mão de obra <HelpTip texto="Só o escritório lança mão de obra, por diária ou por hora. O markup vale sobre diária × dias ou hora × horas." />
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <CampoEmReais id="cfg-diaria" rotulo="Valor da diária" centavos={c.diariaCentavos} aoSalvar={(v) => void salvar(() => orcamentoApi.salvarConfig({ diariaCentavos: v }))} />
          <CampoEmReais id="cfg-hora" rotulo="Valor da hora técnica" centavos={c.horaCentavos} aoSalvar={(v) => void salvar(() => orcamentoApi.salvarConfig({ horaCentavos: v }))} />
          <div>
            <Label htmlFor="cfg-mk-mao">Markup da mão de obra (%)</Label>
            <Input
              id="cfg-mk-mao"
              inputMode="decimal"
              defaultValue={String(c.markupMoPct).replace('.', ',')}
              onBlur={(e) => {
                const v = Number(e.target.value.replace(',', '.'));
                if (Number.isFinite(v) && v !== c.markupMoPct) void salvar(() => orcamentoApi.salvarConfig({ markupMoPct: v }));
              }}
            />
          </div>
        </div>
      </section>

      <section className={GRUPO}>
        <h3 className="mb-3 text-sm font-bold">E-mail e prazos</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CampoDeTexto id="cfg-email-manut" rotulo='E-mail da Manutenção ("Responder para" e cópia)' valor={c.emailManutencao ?? ''} tipo="email" aoSalvar={(v) => void salvar(() => orcamentoApi.salvarConfig({ emailManutencao: v || null }))} />
          <CampoDeTexto id="cfg-email-compras" rotulo="E-mail do Compras (Solicitação de FAT/COMPRA)" valor={c.emailCompras ?? ''} tipo="email" aoSalvar={(v) => void salvar(() => orcamentoApi.salvarConfig({ emailCompras: v || null }))} />
          <div>
            <Label htmlFor="cfg-prazo-retorno">Prazo para o cliente responder (dias)</Label>
            <Input
              id="cfg-prazo-retorno"
              inputMode="numeric"
              defaultValue={String(c.prazoRetornoDias)}
              onBlur={(e) => {
                const v = Number(e.target.value);
                if (Number.isInteger(v) && v !== c.prazoRetornoDias) void salvar(() => orcamentoApi.salvarConfig({ prazoRetornoDias: v }));
              }}
            />
          </div>
          <div className="text-sm text-ink-3 dark:text-ink-dark-3 sm:self-end">O próximo orçamento será o M-{c.proximoNumero}.</div>
        </div>
        <div className="mt-3">
          <Label htmlFor="cfg-texto">Texto do e-mail do orçamento</Label>
          <textarea
            id="cfg-texto"
            rows={3}
            defaultValue={c.textoEmail}
            onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== c.textoEmail && void salvar(() => orcamentoApi.salvarConfig({ textoEmail: e.target.value }))}
            className="w-full rounded-xl border border-line bg-paper-surface px-4 py-2 text-sm text-ink-1 focus:border-red focus:outline-none focus:ring-2 focus:ring-red-soft dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1"
          />
        </div>
      </section>

      <Catalogo
        titulo="Descontos"
        ajuda="Os percentuais que o colaborador pode dar. O desconto vale sobre os produtos e a mão de obra juntos."
        linhas={c.descontos.map((x) => ({ id: x.id, nome: `${String(x.percentual).replace('.', ',')}%`, ativo: x.ativo }))}
        aoLigar={(id, ativo) => void salvar(() => orcamentoApi.ligarNoCatalogo('desconto', id, ativo))}
        novo={{ rotulo: 'Novo desconto (%)', aoCriar: (t) => void salvar(() => orcamentoApi.acrescentarAoCatalogo({ qual: 'desconto', percentual: Number(t.replace(',', '.')) })) }}
      />
      <Catalogo
        titulo="Formas de pagamento"
        ajuda="Cada forma vale para uma faixa do total. Na hora de montar, só aparecem as que valem para o total do orçamento."
        linhas={c.pagamentos.map((p) => ({
          id: p.id,
          nome: `${p.nome} · ${p.maxCentavos === null ? `a partir de ${brl(p.minCentavos)}` : `de ${brl(p.minCentavos)} até ${brl(p.maxCentavos)}`}`,
          ativo: p.ativo,
        }))}
        aoLigar={(id, ativo) => void salvar(() => orcamentoApi.ligarNoCatalogo('pagamento', id, ativo))}
        novoPagamento={(d) => void salvar(() => orcamentoApi.acrescentarAoCatalogo({ qual: 'pagamento', ...d }))}
      />
      <Catalogo
        titulo="Prazos de entrega"
        ajuda="O prazo que vai no orçamento, contado a partir da aprovação. Quem monta escolhe da lista; ninguém digita."
        linhas={c.prazos.map((p) => ({ id: p.id, nome: p.texto, ativo: p.ativo }))}
        aoLigar={(id, ativo) => void salvar(() => orcamentoApi.ligarNoCatalogo('prazo', id, ativo))}
        novo={{ rotulo: 'Novo prazo', aoCriar: (t) => void salvar(() => orcamentoApi.acrescentarAoCatalogo({ qual: 'prazo', texto: t })) }}
      />
    </div>
  );
}

function CampoEmReais({ id, rotulo, centavos, aoSalvar }: { id: string; rotulo: string; centavos: number; aoSalvar: (c: number) => void }) {
  return (
    <div>
      <Label htmlFor={id}>{rotulo}</Label>
      <Input
        id={id}
        inputMode="decimal"
        defaultValue={reaisDoCampo(centavos)}
        onBlur={(e) => {
          const v = reaisStringToCentavos(e.target.value);
          if (v !== centavos) aoSalvar(v);
        }}
      />
    </div>
  );
}

function CampoDeTexto({ id, rotulo, valor, tipo, aoSalvar }: { id: string; rotulo: string; valor: string; tipo: string; aoSalvar: (v: string) => void }) {
  return (
    <div>
      <Label htmlFor={id}>{rotulo}</Label>
      <Input id={id} type={tipo} defaultValue={valor} onBlur={(e) => e.target.value.trim() !== valor && aoSalvar(e.target.value.trim())} />
    </div>
  );
}

/** Um catálogo: os ligados e os desligados à vista (regra de 02/10: quem administra vê os desligados e religa). */
function Catalogo({
  titulo,
  ajuda,
  linhas,
  aoLigar,
  novo,
  novoPagamento,
}: {
  titulo: string;
  ajuda: string;
  linhas: Array<{ id: string; nome: string; ativo: boolean }>;
  aoLigar: (id: string, ativo: boolean) => void;
  novo?: { rotulo: string; aoCriar: (texto: string) => void };
  novoPagamento?: (d: { nome: string; minCentavos: number; maxCentavos: number | null }) => void;
}) {
  const [texto, setTexto] = useState('');
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  return (
    <section className={GRUPO}>
      <h3 className="mb-3 flex items-center gap-1 text-sm font-bold">
        {titulo} <HelpTip texto={ajuda} />
      </h3>
      <ul className="flex flex-col gap-1">
        {linhas.map((l) => (
          <li key={l.id} className="flex min-h-[44px] items-center justify-between gap-2 border-t border-line text-sm first:border-t-0 dark:border-line-dark">
            <span className={cn(!l.ativo && 'text-ink-3 dark:text-ink-dark-3')}>
              {l.nome}
              {!l.ativo ? ' · desligado' : ''}
            </span>
            <Button size="sm" variant="ghost" onClick={() => aoLigar(l.id, !l.ativo)} className="min-h-[44px] md:min-h-0">
              {l.ativo ? 'Desligar' : 'Religar'}
            </Button>
          </li>
        ))}
      </ul>
      {novo ? (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[200px] flex-1">
            <Label htmlFor={`novo-${titulo}`}>{novo.rotulo}</Label>
            <Input id={`novo-${titulo}`} value={texto} onChange={(e) => setTexto(e.target.value)} />
          </div>
          <Button size="sm" variant="secondary" disabled={!texto.trim()} onClick={() => (novo.aoCriar(texto.trim()), setTexto(''))} className="min-h-[44px] md:min-h-0">
            Acrescentar
          </Button>
        </div>
      ) : null}
      {novoPagamento ? (
        <div className="mt-3 grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_120px_120px_auto]">
          <div>
            <Label htmlFor="novo-pagamento-nome">Nova forma</Label>
            <Input id="novo-pagamento-nome" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Ex.: 4x no boleto" />
          </div>
          <div>
            <Label htmlFor="novo-pagamento-min">A partir de</Label>
            <Input id="novo-pagamento-min" value={min} onChange={(e) => setMin(e.target.value)} inputMode="decimal" placeholder="R$ 0,00" />
          </div>
          <div>
            <Label htmlFor="novo-pagamento-max">Até (opcional)</Label>
            <Input id="novo-pagamento-max" value={max} onChange={(e) => setMax(e.target.value)} inputMode="decimal" placeholder="sem teto" />
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={!texto.trim()}
            className="min-h-[44px] md:min-h-0"
            onClick={() => {
              novoPagamento({ nome: texto.trim(), minCentavos: reaisStringToCentavos(min), maxCentavos: max.trim() ? reaisStringToCentavos(max) : null });
              setTexto('');
              setMin('');
              setMax('');
            }}
          >
            Acrescentar
          </Button>
        </div>
      ) : null}
    </section>
  );
}
